import { objectToFormData, useMediaQuery } from '@dnhb/node_package';
import { useTheme } from '@emotion/react';
import {useState, useEffect, useRef, Fragment, FC} from 'react';
import axios from 'axios';
import { queryCache } from 'react-query';
import Button from '../../../../shared/Button';
import Box from '../../../../shared/layouts/Box';
import Flex from '../../../../shared/layouts/Flex';
import UploadBaseModal from './UploadBaseModal';
import ProgressBar from '../../../../shared/ProgressBar';
import { Stack } from '../../../../shared/layouts';
import FileDropzoneV3 from '../../../../shared/FileDropzoneV3';
import CommunicationBar from './CommunicationBar';
import FileList from './FileList';
import SearchInput from '../../../../shared/SearchInput';
import { DocumentData, UploadModalFile } from "../../../../../library/uploadModal";
import Textarea from '../../../../shared/Textarea';
import {useTranslation} from "react-i18next";
// import SearchIcon from 'imagesUwKluis/svg/search.svg';

interface UploadModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  documentData: DocumentData;
  selectedFiles: UploadModalFile[];
  availableFiles: UploadModalFile[];
  connectedFiles: UploadModalFile[];
  handleMoveToSelected: (FileProps) => void;
  handleMoveToAvailable: (FileProps) => void;
  hasEditRights: boolean;
  handleSubmit: (comment: string) => void;
  getAvailableFiles: (query: string, cb?: () => void) => void;
  checkedForConnectedFiles: boolean;
}

const UploadModal: FC<UploadModalProps> = ({
  isOpen,
  onRequestClose,
  documentData = {},
  selectedFiles = [],
  availableFiles = [],
  connectedFiles = [],
  handleMoveToSelected,
  handleMoveToAvailable,
  hasEditRights,
  handleSubmit,
  getAvailableFiles,
  checkedForConnectedFiles,
}) => {
  const { t } = useTranslation();
  const { status, name, description, rejection_reason, comment = '' } = documentData;
  const { breakpoints } = useTheme();
  const isMinDesktop = useMediaQuery(
    `(min-width: ${breakpoints.ml ?? '56,25rem'})`
  );
  const scrollContainer = useRef(null);
  const uploadBox = useRef(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSearching, setMobileSearching] = useState(false);
  const [initiallyLoaded, setInitiallyLoaded] = useState(false)
  const [errors, setErrors] = useState([]);
  const [optionalComment, setOptionalComment] = useState(comment);

  useEffect(() => {
    if (scrollContainer.current || uploadBox.current) {
      if (mobileSearching) {
        scrollContainer.current.scrollTo(0, 0)
      } else {
        uploadBox.current.scrollIntoView();
      }
    }
  }, [mobileSearching]);

  useEffect(() => {
    if (checkedForConnectedFiles) {
      const timeoutId = setTimeout(() => {
        getAvailableFiles(searchQuery, () => setInitiallyLoaded(true));
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [checkedForConnectedFiles, searchQuery]);

  const onUpload = async files => {
    setIsUploading(true);
    const uploadConnectPromises = [];

    files.map(file =>
      uploadConnectPromises.push(
        axios.post(
          '/files', objectToFormData({ newFile: file, name: file.name }),
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
      )
    );

    Promise.allSettled(uploadConnectPromises).then(fileResponses => {
      queryCache.invalidateQueries('documentRequests');
      const rejections = [];

      files.map((file, i) => {
        const { name } = file;
        const promise = fileResponses[i];

        if (promise.status === 'rejected') {
          const response = promise.reason.response;
          const fileErrors = response.status === 422 ? response.data.errors.newFile : [];
          rejections.push({ name, fileErrors });
        }

        if (promise.status === 'fulfilled') {
          const file = promise.value.data;
          file.canDelete = true;
          handleMoveToSelected(file);
        }
      });

      setErrors(rejections);
      setIsUploading(false);
    });
  };

  const cancelAndClose = () => {
    selectedFiles.map(file => {
      if (file.canDelete) {
        axios.delete('/files/' + file.fileId);
      }
    });

    onRequestClose();
  };

  const isRejected = status === 'rejected';
  const mainColor = isRejected ? 'alert' : 'primary';
  const fileLists = (
    <Stack space="m">
      <FileList
        data={selectedFiles}
        header={t('Geselecteerde bestanden') + ':'}
        variant="selected"
        fileAction={handleMoveToAvailable}
      />
      <FileList
        data={connectedFiles}
        header={
          (isRejected ? t('Afgekeurde bestanden') : t('Eerder gedeelde bestanden')) + ':'
        }
        variant={isRejected ? 'rejected' : 'shared'}
      />
      {/*{hasEditRights && (*/}
      {/*  <FileList*/}
      {/*    data={availableFiles}*/}
      {/*    allowEmpty*/}
      {/*    isLoadingWhileEmpty={!initiallyLoaded}*/}
      {/*    header={*/}
      {/*      <Fragment>*/}
      {/*        <span>Recent geüpload:</span>*/}
      {/*        {isMinDesktop ? (*/}
      {/*          <SearchInput*/}
      {/*            value={searchQuery}*/}
      {/*            onChange={setSearchQuery}*/}
      {/*            width="280px"*/}
      {/*            css={{ marginRight: 17 }}*/}
      {/*          />*/}
      {/*        ) : (*/}
      {/*          <SearchIcon*/}
      {/*            width="16px"*/}
      {/*            height="16px"*/}
      {/*            css={theme => ({ fill: theme.colors.secondaryButtonActive })}*/}
      {/*            onClick={() => setMobileSearching(true)}*/}
      {/*          />*/}
      {/*        )}*/}
      {/*      </Fragment>*/}
      {/*    }*/}
      {/*    fileAction={handleMoveToSelected}*/}
      {/*  />*/}
      {/*)}*/}
    </Stack>
  );

  return (
    <UploadBaseModal isOpen={isOpen} onRequestClose={cancelAndClose}>
      {isMinDesktop ? (
        <Fragment>
          <Flex height="100%" fontScale="s">
            <CommunicationBar
              {...{
                isMinDesktop,
                mainColor,
                isRejected,
                name,
                description,
                rejection_reason
              }}
            />
            <Flex flexDirection="column" p="l" width="calc(100% - 320px)" bg="white">
              {hasEditRights && (
                <FileDropzoneV3
                  width="100%"
                  minHeight="176px"
                  loading={isUploading}
                  onDrop={files => onUpload(files)}
                  errors={errors}
                  mb="m"
                  disabled={false}
                />
              )}
              <Stack space="l" flexGrow={1} css={{ minHeight: '176px', overflow: 'auto' }}>
                {fileLists}
              </Stack>
              <Flex flexDirection={'column'} flexGrow={1} mt={'l'}>
                <Box
                  fontScale="m"
                  fontWeight="bold"
                  mb="s"
                >
                  {t('Notitie')}:
                </Box>
                <Textarea
                  css={{ flexGrow: 1, 'textarea': { height: '100%', maxHeight: '240px' } }}
                  value={optionalComment}
                  placeholder={t('Optioneel')}
                  onChange={({ target }) => setOptionalComment(target.value)}
                  readOnly={!hasEditRights}
                />
              </Flex>
              <Flex justifyContent="flex-end" marginTop="l">
                <Button
                  fontScale="m"
                  variant="primary-outline"
                  buttonSize="small"
                  onClick={cancelAndClose}
                >
                  {t('Annuleren')}
                </Button>
                {selectedFiles.length > 0 && (
                  <Button
                    fontScale="m"
                    buttonSize="small"
                    onClick={() => {
                      setIsSubmitting(true);
                      handleSubmit(optionalComment);
                    }}
                    css={{ marginLeft: 16 }}
                    disabled={isSubmitting}
                  >
                    {t('Bestanden delen')}
                  </Button>
                )}
              </Flex>
            </Flex>
          </Flex>
          <ProgressBar
            color={mainColor}
            css={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              width: '100%'
            }}
          />
        </Fragment>
      ) : (
        <Flex flexDirection="column" height="calc(100% - 72px)" fontScale="s">
          {mobileSearching && (
            <Flex justifyContent="center" m="m">
              <SearchInput
                width="100%"
                value={searchQuery}
                onChange={setSearchQuery}
              />
            </Flex>
          )}
          <Box flexGrow={1} css={{ overflow: 'auto' }} ref={scrollContainer}>
            {mobileSearching ? (
              <FileList
                data={availableFiles}
                isLoadingWhileEmpty={!initiallyLoaded}
                allowEmpty
                fileAction={file => {
                  setMobileSearching(false);
                  handleMoveToSelected(file);
                }}
              />
            ) : (
              <Fragment>
                <CommunicationBar
                  {...{
                    isMinDesktop,
                    mainColor,
                    isRejected,
                    name,
                    description,
                    rejection_reason
                  }}
                />
                <Box p="m" ref={uploadBox}>
                  {hasEditRights && (
                    <FileDropzoneV3
                      width="100%"
                      minHeight="48px"
                      loading={isUploading}
                      errors={errors}
                      onDrop={files => onUpload(files)}
                      disabled={false}
                    />
                  )}
                </Box>
                {fileLists}
                <Box p={'m'}>
                  <Box
                    fontScale="m"
                    fontWeight="bold"
                    mb="s"
                  >
                    {t('Notitie')}:
                  </Box>
                  <Textarea
                    value={optionalComment}
                    placeholder={t('Optioneel')}
                    onChange={({ target }) => setOptionalComment(target.value)}
                    readOnly={!hasEditRights}
                  />
                </Box>
              </Fragment>
            )}
          </Box>
          <Box p="24px 16px">
            {mobileSearching ? (
              <Button
                width="100%"
                fontScale="m"
                variant="primary-outline"
                buttonSize="small"
                onClick={() => {
                  setSearchQuery('');
                  setMobileSearching(false);
                }}
              >
                {t('Terug')}
              </Button>
            ) : (
              <Stack space="s">
                {selectedFiles.length > 0 && (
                  <Button
                    fontScale="m"
                    buttonSize="small"
                    onClick={() => {
                      setIsSubmitting(true);
                      handleSubmit(optionalComment);
                    }}
                    width="100%"
                    disabled={isSubmitting}
                  >
                    {t('Bestanden delen')}
                  </Button>
                )}
                <Button
                  width="100%"
                  fontScale="m"
                  variant="primary-outline"
                  buttonSize="small"
                  onClick={cancelAndClose}
                >
                  {t('Annuleren')}
                </Button>
              </Stack>
            )}
          </Box>
        </Flex>
      )}
    </UploadBaseModal>
  );
};

export default UploadModal;
